import React from 'react'
import PropTypes from 'prop-types'

function parseInline(text) {
  return text.split(/(`[^`]+`|\*\*[^*]+\*\*)/).map((seg, i) => {
    if (seg.startsWith('**') && seg.endsWith('**')) {
      return <strong key={i}>{seg.slice(2, -2)}</strong>
    }
    if (seg.startsWith('`') && seg.endsWith('`')) {
      return (
        <code key={i} className="rounded px-1 bg-white border" style={{ fontSize: '0.8em' }}>
          {seg.slice(1, -1)}
        </code>
      )
    }
    return seg
  })
}

const TABLE_ROW = /^\s*\|/
const DELIMITER_ROW = /^\s*\|?\s*:?-+:?\s*(\|\s*:?-+:?\s*)*\|?\s*$/
const NUMERIC_CELL = /^[-+(]?[$€£]?\s?\d[\d,]*(\.\d+)?\s?(%|[KMB])?\)?(\s|$)/

const isTableStart = (lines, i) => TABLE_ROW.test(lines[i]) && i + 1 < lines.length && DELIMITER_ROW.test(lines[i + 1])

// A pipe escaped as \| stays inside its cell.
const splitRow = (line) =>
  line
    .trim()
    .replace(/^\|/, '')
    .replace(/\|$/, '')
    .replace(/\\\|/g, '\u0000')
    .split('|')
    .map((cell) => cell.replace(/\u0000/g, '|').trim())

const delimiterAlign = (cell) => {
  if (cell.startsWith(':') && cell.endsWith(':')) return 'center'
  if (cell.endsWith(':')) return 'right'
  if (cell.startsWith(':')) return 'left'
  return null
}

// The delimiter row's alignment where given; otherwise a column of figures lines up on the right.
const columnAlignments = (delimiters, rows) =>
  delimiters.map((cell, col) => {
    const explicit = delimiterAlign(cell)
    if (explicit) return explicit
    const values = rows.map((row) => row[col]).filter(Boolean)
    return values.length > 0 && values.every((value) => NUMERIC_CELL.test(value)) ? 'right' : 'left'
  })

const ALIGN_CLASS = { left: 'text-start', center: 'text-center', right: 'text-end text-nowrap' }

const MarkdownTable = ({ header, delimiters, rows }) => {
  const width = header.length
  const body = rows.map((row) => Array.from({ length: width }, (_, col) => row[col] ?? ''))
  const align = columnAlignments(delimiters.slice(0, width), body)

  return (
    <div className="table-responsive my-1">
      <table className="table table-sm table-bordered bg-white small mb-0" data-testid="markdown-table">
        <thead>
          <tr>
            {header.map((cell, col) => (
              <th key={col} className={`${ALIGN_CLASS[align[col]]} text-nowrap`}>
                {parseInline(cell)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, r) => (
            <tr key={r}>
              {row.map((cell, col) => (
                <td key={col} className={ALIGN_CLASS[align[col]]}>
                  {parseInline(cell)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// Renders the small markdown subset the assistants write: **bold**, `code`, bullet and numbered
// lists, ## / ### headings and pipe tables. Anything else is plain text, never HTML.
function MarkdownMessage({ text }) {
  const lines = (text || '').split('\n')
  const blocks = []
  let i = 0
  while (i < lines.length) {
    const line = lines[i]

    if (isTableStart(lines, i)) {
      const header = splitRow(line)
      const delimiters = splitRow(lines[i + 1])
      const start = i
      const rows = []
      i += 2
      while (i < lines.length && TABLE_ROW.test(lines[i])) {
        rows.push(splitRow(lines[i]))
        i++
      }
      blocks.push(<MarkdownTable key={`table${start}`} header={header} delimiters={delimiters} rows={rows} />)
      continue
    }

    if (/^[-*] /.test(line)) {
      const items = []
      while (i < lines.length && /^[-*] /.test(lines[i])) {
        items.push(<li key={i}>{parseInline(lines[i].slice(2))}</li>)
        i++
      }
      blocks.push(<ul key={`ul${i}`} className="mb-1 ps-3" style={{ marginTop: 2 }}>{items}</ul>)
      continue
    }

    if (/^\d+\. /.test(line)) {
      const items = []
      while (i < lines.length && /^\d+\. /.test(lines[i])) {
        items.push(<li key={i}>{parseInline(lines[i].replace(/^\d+\. /, ''))}</li>)
        i++
      }
      blocks.push(<ol key={`ol${i}`} className="mb-1 ps-3" style={{ marginTop: 2 }}>{items}</ol>)
      continue
    }

    if (/^### /.test(line)) {
      blocks.push(<div key={i} className="fw-semibold mt-2 mb-1" style={{ fontSize: '0.85rem' }}>{parseInline(line.slice(4))}</div>)
    } else if (/^## /.test(line)) {
      blocks.push(<div key={i} className="fw-bold mt-2 mb-1" style={{ fontSize: '0.9rem' }}>{parseInline(line.slice(3))}</div>)
    } else if (!line.trim()) {
      blocks.push(<div key={`sp${i}`} style={{ height: '0.4em' }} />)
    } else {
      blocks.push(<div key={i}>{parseInline(line)}</div>)
    }
    i++
  }
  return <>{blocks}</>
}

MarkdownMessage.propTypes = {
  text: PropTypes.string
}

export default MarkdownMessage
